import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sun, Moon, Monitor, Check } from 'lucide-react';

import { useTheme } from '../../providers/ThemeProvider';
import './ThemeModal.css';

const ThemeModal = ({ isOpen, onClose }) => {
  const { theme, actualTheme, setThemeMode, isDark } = useTheme();
  const [selectedTheme, setSelectedTheme] = useState(theme);

  const themes = [
    {
      id: 'light',
      name: 'Claro',
      description: 'Tema claro para ambientes bem iluminados',
      icon: Sun,
      preview: '☀️',
    },
    {
      id: 'dark',
      name: 'Escuro',
      description: 'Tema escuro para reduzir fadiga visual',
      icon: Moon,
      preview: '🌙',
    },
    {
      id: 'system',
      name: 'Sistema',
      description: 'Segue as configurações do seu dispositivo',
      icon: Monitor,
      preview: '🖥️',
    },
  ];

  const handleThemeSelect = themeId => {
    setSelectedTheme(themeId);
  };

  const handleApply = () => {
    setThemeMode(selectedTheme);
    onClose();
  };

  const getCurrentThemeInfo = () => {
    const currentTheme = themes.find(t => t.id === theme);
    return currentTheme || themes[0];
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="theme-modal-overlay" onClick={onClose}>
      <div className="theme-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="theme-modal__header">
          <div className="theme-modal__header-content">
            <h2 className="theme-modal__title">
              <Monitor size={20} />
              Escolher Tema
            </h2>
            <p className="theme-modal__subtitle">
              Selecione o tema que melhor se adapta ao seu ambiente
            </p>
          </div>
          <button className="theme-modal__close" onClick={onClose} aria-label="Fechar modal">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="theme-modal__content">
          <div className="theme-modal__themes">
            {themes.map(themeOption => {
              const Icon = themeOption.icon;
              const isSelected = selectedTheme === themeOption.id;
              const isCurrent = theme === themeOption.id;

              return (
                <div
                  key={themeOption.id}
                  className={`theme-modal__theme-card ${isSelected ? 'theme-modal__theme-card--selected' : ''} ${isCurrent ? 'theme-modal__theme-card--current' : ''}`}
                  onClick={() => handleThemeSelect(themeOption.id)}
                >
                  <div className="theme-modal__theme-preview">
                    <div
                      className={`theme-modal__preview-icon theme-modal__preview-icon--${themeOption.id}`}
                    >
                      <Icon size={24} />
                    </div>
                    <div className="theme-modal__preview-bg"></div>
                  </div>

                  <div className="theme-modal__theme-info">
                    <div className="theme-modal__theme-header">
                      <h3 className="theme-modal__theme-name">{themeOption.name}</h3>
                      {isCurrent && (
                        <span className="theme-modal__current-badge">
                          <Check size={12} />
                          Atual
                        </span>
                      )}
                    </div>
                    <p className="theme-modal__theme-description">{themeOption.description}</p>
                    {themeOption.id === 'system' && (
                      <p className="theme-modal__system-info">
                        Sistema detectado:{' '}
                        <strong>{actualTheme === 'dark' ? 'Escuro' : 'Claro'}</strong>
                      </p>
                    )}
                  </div>

                  <div className="theme-modal__theme-selector">
                    <div
                      className={`theme-modal__radio ${isSelected ? 'theme-modal__radio--checked' : ''}`}
                    >
                      {isSelected && <div className="theme-modal__radio-dot"></div>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="theme-modal__footer">
          <button className="theme-modal__button theme-modal__button--secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="theme-modal__button theme-modal__button--primary"
            onClick={handleApply}
            disabled={selectedTheme === theme}
          >
            Aplicar Tema
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ThemeModal;
